import { createContext, useMemo, useState, type ReactNode } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";

import { useInitialXpointer } from "lib/bookmarks/use-auto-bookmark";
import { getContent } from "lib/content/get-content";
import {
  getRemainingPartIndices,
  useProgressiveContent,
} from "lib/content/progressive-content";
import { getConfig } from "lib/get-config";
import { BUFFER_SIZE } from "lib/layout/use-content-layout";
import {
  chapterByOrder,
  parseXpath,
  partIndexByXpath,
} from "lib/navigation/paths";
import { useToc } from "lib/toc/use-toc";
import type { ContentNode } from "lib/types";

interface ContentParts {
  findPartIndexByXpointer: (xpointer: string) => number;
  setCurrentPartIndex: (partIndex: number) => void;
  currentPartIndex: number;
}

interface ContentContextType {
  content: ContentNode[][];
  isFullyLoaded: boolean;
  requestPart: (partIndex: number) => void;
  initialContentParts: ContentNode[][];
  initialPartIndex: number;
  contentParts: ContentParts | null;
  currentPartIndex: number;
  setCurrentPartIndex: (index: number) => void;
}

export const ContentContext = createContext<ContentContextType | undefined>(
  undefined
);
ContentContext.displayName = "ContentContext";

export function ContentProvider({ children }: { children: ReactNode }) {
  const { toc } = useToc();
  const { art, chapter } = getConfig();
  const { autoBookmark } = useInitialXpointer();

  const initialPartIndex = useMemo(() => {
    if (!toc?.Parts) return 0;

    // Если указана глава в URL, загружаем контент вокруг неё
    if (chapter) {
      const target = chapterByOrder(toc, chapter);
      if (target) {
        const partIndex = toc.Parts.findIndex(
          p => target.s >= p.s && target.s <= p.e
        );
        if (partIndex !== -1) return partIndex;
      }
    }

    if (!autoBookmark?.data?.xpointer) return 0;

    const xpath = parseXpath(autoBookmark.data.xpointer);
    const partIndex = partIndexByXpath(toc.Parts, xpath);

    return partIndex !== -1 ? partIndex : 0;
  }, [toc, autoBookmark, chapter]);

  const totalParts = toc?.Parts?.length ?? 0;

  const bufferStart = Math.max(0, initialPartIndex - BUFFER_SIZE);
  const bufferEnd = Math.min(totalParts - 1, initialPartIndex + BUFFER_SIZE);
  const initialPartsToLoad = toc?.Parts
    ? toc.Parts.slice(bufferStart, bufferEnd + 1)
    : [];

  const { data: initialContentParts = [] } = useSuspenseQuery({
    queryKey: ["content-initial", art, initialPartsToLoad],
    queryFn: async () => {
      if (initialPartsToLoad.length === 0) return [];
      const results = await getContent(initialPartsToLoad);
      return results.map(result => (Array.isArray(result) ? result : [result]));
    },
  });

  // Части, не вошедшие в начальный буфер
  const remainingIndices = getRemainingPartIndices(
    totalParts,
    bufferStart,
    bufferEnd
  );
  const remainingParts = toc?.Parts
    ? remainingIndices.map(i => toc.Parts![i])
    : [];

  const { content, isFullyLoaded, requestPart } = useProgressiveContent({
    art,
    remainingParts,
    remainingIndices,
    totalParts,
    initialParts: initialContentParts,
    bufferStart,
  });

  const [currentPartIndex, _setCurrentPartIndex] = useState(
    initialPartIndex >= 0 ? initialPartIndex : 0
  );

  function setCurrentPartIndex(partIndex: number) {
    const validIndex = Math.max(0, Math.min(partIndex, totalParts - 1));
    if (validIndex !== currentPartIndex) {
      _setCurrentPartIndex(validIndex);
    }
  }

  function findPartIndexByXpointer(xpointer: string): number {
    if (!toc?.Parts) return currentPartIndex;

    const xpath = parseXpath(xpointer);
    const partIndex = partIndexByXpath(toc.Parts, xpath);

    if (partIndex === -1) {
      console.warn(
        `[ContentParts] XPointer "${xpointer}" not found in any part, using current part ${currentPartIndex}`
      );
      return currentPartIndex;
    }

    return partIndex;
  }

  const contentParts: ContentParts | null =
    toc?.Parts && totalParts > 0
      ? {
          findPartIndexByXpointer,
          setCurrentPartIndex,
          currentPartIndex,
        }
      : null;

  return (
    <ContentContext
      value={{
        content,
        isFullyLoaded,
        requestPart,
        currentPartIndex,
        setCurrentPartIndex,
        contentParts,
        initialContentParts,
        initialPartIndex,
      }}
    >
      {children}
    </ContentContext>
  );
}
